import { PropsWithChildren, useCallback, useRef, useState } from "react";
import {
  backButtonWrapper,
  backButton,
  inputsArea,
  formFooter,
  leftFooterComponents,
  loadingText,
  errorTextStyle,
  textareaWrapper,
  relativeWrapper,
  textareaWrapperOpen,
  form,
} from "./style.css";
import { AnimatePresence } from "motion/react";
import { ChevronLeftIcon } from "../../../../shared/components/icons/ChevronLeftIcon";
import { SimpleRevealingAnimation } from "../../../../shared/components/simple-revealing-animation";
import { SubmitView } from "./submit-view";
import { TextLink } from "../../../../shared/components/link/TextLink";
import { isNotEmpty, isValidEmail } from "../../../../shared/validation/rules";
import { Input } from "../../../../shared/components/input";
import { Textarea } from "../../../../shared/components/textarea";
import { SaveButton } from "./save-button";
import classNames from "classnames";
import text from "./resources/text.json";
import { FormData, sendFormData } from "./requests/sendFormData";
import { Language } from "../../../../providers/language/types";

export enum FormView {
  FORM = "form",
  MESSAGE = "message",
  SUBMIT = "submit",
  HIDDEN = "hidden",
}

const defaultFormData: FormData = {
  name: "",
  company: "",
  email: "",
  message: "",
};

export const FormContainer = ({
  children,
  lang = "en",
}: PropsWithChildren<{ lang?: Language }>) => {
  const [formView, setFormView] = useState<FormView>(FormView.FORM);

  const [isEditModeTextArea, setIsEditModeTextArea] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validation, setValidation] = useState<Partial<
    Record<keyof FormData, boolean>
  > | null>(null);
  const [errorText, setErrorText] = useState<string>("");

  const [formData, setFormData] = useState<FormData>(defaultFormData);

  const {
    name,
    company,
    email,
    message,
    optional,
    send,
    goBack,
    saveMessage,
    errors,
    submittingText,
  } = text[lang as keyof typeof text];

  // temporary function
  const submit = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      if (validateDate(formData)) {
        return;
      }

      setValidation(null);
      setErrorText("");
      setIsLoading(true);

      (async () => {
        try {
          const response = await sendFormData(formData, lang);

          if (response.ok) {
            setFormView(FormView.HIDDEN);
            setFormData(defaultFormData);
            return;
          }

          if (response.status === 422) {
            throw new Error(errors.incorrectData);
          }

          throw new Error(errors.somethingWrong);
        } catch (error: unknown) {
          if (error instanceof Error) {
            setErrorText(error.message);
            return;
          }

          if (typeof error === "string") {
            setErrorText(error);
            return;
          }

          setErrorText(errors.somethingWrong);
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [formData, formView],
  );

  const validateDate = (data: FormData) => {
    const validateErrors: Partial<Record<keyof FormData, boolean>> = {};

    if (
      !isNotEmpty(data.name) ||
      !isNotEmpty(data.email) ||
      !isNotEmpty(data.company)
    ) {
      validateErrors["name"] = !isNotEmpty(data.name);
      validateErrors["email"] = !isNotEmpty(data.email);
      validateErrors["company"] = !isNotEmpty(data.company);

      setValidation(validateErrors);
      setErrorText(errors.fillFields);

      return true;
    }

    if (!isValidEmail(data.email)) {
      validateErrors["email"] = true;

      setValidation(validateErrors);
      setErrorText(errors.incorrectEmail);

      return true;
    }

    return false;
  };

  const openTextArea = () => {
    setIsEditModeTextArea(true);
    setFormView(FormView.MESSAGE);
    textAreaRef.current?.focus();
  };

  const closeTextArea = (event?: React.MouseEvent) => {
    event?.preventDefault();

    setIsEditModeTextArea(false);
    setFormView(FormView.FORM);
  };

  const changeFormData = (property: keyof FormData, value: string) => {
    setErrorText("");
    setFormData((prevValue) => ({ ...prevValue, [property]: value }));
    setValidation((prevValue) => {
      if (prevValue === null) return null;

      const { [property]: _dropMe, ...rest } = prevValue;
      return rest;
    });
  };

  return (
    <div>
      <div className={backButtonWrapper}>
        <AnimatePresence
          mode="wait"
          custom={{
            animationDirection: formView === FormView.FORM ? "right" : "left",
          }}
        >
          {formView === FormView.MESSAGE && (
            <SimpleRevealingAnimation duration={0.5}>
              <TextLink
                className={backButton}
                noUnderline
                onClick={closeTextArea}
              >
                <ChevronLeftIcon />
                {goBack}
              </TextLink>
            </SimpleRevealingAnimation>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence
        mode="wait"
        custom={{
          animationDirection: "up",
        }}
        onExitComplete={() => {
          if (formView === FormView.HIDDEN) {
            setFormView(FormView.SUBMIT);
          }
        }}
      >
        {formView !== FormView.HIDDEN && formView !== FormView.SUBMIT && (
          <SimpleRevealingAnimation duration={0.5}>
            <div>{children}</div>
            <form className={form}>
              <div className={inputsArea}>
                <Input
                  value={formData.name}
                  onChange={(e) => changeFormData("name", e.target.value)}
                  placeholder={name}
                  isError={validation?.name}
                />
                <Input
                  value={formData.company}
                  onChange={(e) => changeFormData("company", e.target.value)}
                  placeholder={company}
                  isError={validation?.company}
                />
                <Input
                  value={formData.email}
                  onChange={(e) => changeFormData("email", e.target.value)}
                  placeholder={email}
                  type="email"
                  isError={validation?.email}
                />
              </div>

              <div className={relativeWrapper}>
                <div
                  onClick={openTextArea}
                  className={classNames(textareaWrapper, {
                    [textareaWrapperOpen]: isEditModeTextArea,
                  })}
                >
                  <Textarea
                    ref={textAreaRef}
                    value={formData.message}
                    onChange={(e) => changeFormData("message", e.target.value)}
                    placeholder={message}
                    isEditMode={isEditModeTextArea}
                    onFocus={openTextArea}
                    optionalText={optional}
                  />
                </div>
              </div>

              <div className={formFooter}>
                <div className={leftFooterComponents}>
                  <SaveButton
                    onSubmit={
                      formView === FormView.FORM ? submit : closeTextArea
                    }
                    formView={formView}
                    isLoading={isLoading}
                    sendText={send}
                    saveText={saveMessage}
                  />

                  <AnimatePresence
                    mode="wait"
                    custom={{
                      animationDirection: "left",
                    }}
                  >
                    {isLoading && (
                      <SimpleRevealingAnimation duration={0.2}>
                        <div className={loadingText}>{submittingText}</div>
                      </SimpleRevealingAnimation>
                    )}
                  </AnimatePresence>
                </div>

                <AnimatePresence
                  mode="wait"
                  custom={{
                    animationDirection: "left",
                  }}
                >
                  {Boolean(errorText) && (
                    <SimpleRevealingAnimation duration={0.2}>
                      <div className={errorTextStyle}>{errorText}</div>
                    </SimpleRevealingAnimation>
                  )}
                </AnimatePresence>
              </div>
            </form>
          </SimpleRevealingAnimation>
        )}
      </AnimatePresence>

      <AnimatePresence
        mode="wait"
        custom={{
          animationDirection: "down",
        }}
        onExitComplete={() => {
          if (formView === FormView.HIDDEN) {
            setFormView(FormView.FORM);
          }
        }}
      >
        {formView === FormView.SUBMIT && (
          <SubmitView changeFormView={() => setFormView(FormView.HIDDEN)} />
        )}
      </AnimatePresence>
    </div>
  );
};
